import type { OrgChatAttachmentInput } from '@/components/org-chat/org-chat-types';
import type { FileChipData } from '@/components/tiptap';
import type { Id } from '@/convex/_generated/dataModel';
import { inferWorkspaceFileMimeType } from '@/components/org-chat/composer-upload-accept';
import {
  isComposerUploadChip,
  isCustomReferenceChip,
  isSelectedImageChip,
  isSelectedWorkspaceFileChip,
  isSelectedStoredFileChip,
  customReferenceFieldsFromUploadId,
  isCustomReferenceAttachment,
  applyCustomReferenceFields,
} from './org-chat-api-attachment';
export function reconcileComposerUploadAttachments(
  attachments: Array<OrgChatAttachmentInput>,
  chips: Array<FileChipData>
): Array<OrgChatAttachmentInput> {
  const next = [...attachments];
  const existingUploadIds = new Set(
    attachments
      .map((attachment) => attachment.uploadId)
      .filter((uploadId): uploadId is string => Boolean(uploadId))
  );
  const existingStorageKeys = new Set(
    attachments
      .map((attachment) => attachment.storageKey?.trim())
      .filter((storageKey): storageKey is string => Boolean(storageKey))
  );
  const existingStorageIds = new Set(
    attachments
      .map((attachment) => attachment.storageId)
      .filter((storageId): storageId is Id<'_storage'> => Boolean(storageId))
      .map(String)
  );
  const existingWorkspaceFileIds = new Set(
    attachments
      .map((attachment) => attachment.workspaceFileId)
      .filter((fileId): fileId is Id<'workspaceFiles'> => Boolean(fileId))
      .map(String)
  );
  const existingVfsPaths = new Set(
    attachments
      .map((attachment) => attachment.vfsPath?.trim())
      .filter((path): path is string => Boolean(path))
  );

  for (const chip of chips) {
    const storageKey = chip.storageKey?.trim();
    const isDirectUpload = isComposerUploadChip(chip);
    const isCustomReference = isCustomReferenceChip(chip);
    const isSelectedImage = isSelectedImageChip(chip);
    const isSelectedWorkspaceFile = isSelectedWorkspaceFileChip(chip);
    const isSelectedStoredFile = isSelectedStoredFileChip(chip);
    const storageId = chip.storageId?.trim();
    const workspaceFileId = chip.workspaceFileId?.trim();
    const vfsPath = chip.vfsPath?.trim();
    const customReferenceFields = customReferenceFieldsFromUploadId(
      chip.uploadId
    );
    const resolvedVfsPath = vfsPath ?? customReferenceFields?.vfsPath;
    const existingUploadIndex = next.findIndex(
      (attachment) => attachment.uploadId === chip.uploadId
    );
    const existing = next[existingUploadIndex];
    if (
      existing &&
      (isDirectUpload ||
        isCustomReference ||
        isSelectedImage ||
        isSelectedWorkspaceFile ||
        isSelectedStoredFile)
    ) {
      const shouldUpgradeExisting =
        existing.isUploading === true ||
        (!existing.storageKey && Boolean(storageKey)) ||
        (!existing.storageId && Boolean(storageId)) ||
        (!existing.workspaceFileId && Boolean(workspaceFileId)) ||
        (!existing.vfsPath && Boolean(resolvedVfsPath)) ||
        (isCustomReference && !isCustomReferenceAttachment(existing));
      if (!shouldUpgradeExisting) continue;
      const inferredMimeType =
        isCustomReference || isSelectedImage || isSelectedWorkspaceFile
          ? inferWorkspaceFileMimeType({
              path: resolvedVfsPath || chip.filename,
              mimeType: chip.mimeType,
            })
          : chip.mimeType;
      next[existingUploadIndex] = applyCustomReferenceFields(
        {
          ...existing,
          kind: inferredMimeType.startsWith('image/') ? 'image' : chip.kind,
          filename: chip.filename,
          mimeType: inferredMimeType,
          sizeBytes: chip.sizeBytes,
          ...(storageKey ? { storageKey } : {}),
          ...(chip.storageId
            ? { storageId: chip.storageId as Id<'_storage'> }
            : {}),
          ...(chip.url ? { url: chip.url } : {}),
          ...(chip.previewUrl ? { previewUrl: chip.previewUrl } : {}),
          ...(workspaceFileId
            ? { workspaceFileId: workspaceFileId as Id<'workspaceFiles'> }
            : {}),
          ...(resolvedVfsPath ? { vfsPath: resolvedVfsPath } : {}),
          isUploading: false,
          source: isDirectUpload ? 'upload' : 'selected',
        },
        chip.uploadId
      );
      if (storageKey) existingStorageKeys.add(storageKey);
      if (workspaceFileId) existingWorkspaceFileIds.add(workspaceFileId);
      if (resolvedVfsPath) existingVfsPaths.add(resolvedVfsPath);
      continue;
    }
    if (
      isCustomReference &&
      resolvedVfsPath &&
      existingVfsPaths.has(resolvedVfsPath) &&
      !existingUploadIds.has(chip.uploadId)
    ) {
      const existingPathIndex = next.findIndex(
        (attachment) => attachment.vfsPath === resolvedVfsPath
      );
      const existingPathAttachment = next[existingPathIndex];
      if (existingPathAttachment) {
        next[existingPathIndex] = applyCustomReferenceFields(
          existingPathAttachment,
          chip.uploadId
        );
        continue;
      }
    }
    if (
      (!isDirectUpload &&
        !isCustomReference &&
        !isSelectedImage &&
        !isSelectedWorkspaceFile &&
        !isSelectedStoredFile) ||
      existingUploadIds.has(chip.uploadId) ||
      (storageKey ? existingStorageKeys.has(storageKey) : false) ||
      (storageId ? existingStorageIds.has(storageId) : false) ||
      (workspaceFileId
        ? existingWorkspaceFileIds.has(workspaceFileId)
        : false) ||
      (resolvedVfsPath ? existingVfsPaths.has(resolvedVfsPath) : false)
    ) {
      continue;
    }

    const inferredMimeType =
      isCustomReference || isSelectedImage || isSelectedWorkspaceFile
        ? inferWorkspaceFileMimeType({
            path: resolvedVfsPath || chip.filename,
            mimeType: chip.mimeType,
          })
        : chip.mimeType;
    next.push(
      applyCustomReferenceFields(
        {
          kind: inferredMimeType.startsWith('image/') ? 'image' : chip.kind,
          filename: chip.filename,
          mimeType: inferredMimeType,
          sizeBytes: chip.sizeBytes,
          ...(storageKey ? { storageKey } : {}),
          ...(chip.storageId
            ? { storageId: chip.storageId as Id<'_storage'> }
            : {}),
          ...(chip.url ? { url: chip.url } : {}),
          ...(chip.previewUrl ? { previewUrl: chip.previewUrl } : {}),
          ...(workspaceFileId
            ? { workspaceFileId: workspaceFileId as Id<'workspaceFiles'> }
            : {}),
          ...(resolvedVfsPath ? { vfsPath: resolvedVfsPath } : {}),
          uploadId: chip.uploadId,
          isUploading: false,
          source: isDirectUpload ? 'upload' : 'selected',
        },
        chip.uploadId
      )
    );
    existingUploadIds.add(chip.uploadId);
    if (storageKey) existingStorageKeys.add(storageKey);
    if (storageId) existingStorageIds.add(storageId);
    if (workspaceFileId) existingWorkspaceFileIds.add(workspaceFileId);
    if (resolvedVfsPath) existingVfsPaths.add(resolvedVfsPath);
  }

  return next;
}

export type PresetUploadCategory = 'product' | 'style' | 'color';

export type StyleReferenceType = 'design' | 'pattern';

export function getPresetUploadReferenceCategory(
  category: PresetUploadCategory,
  styleType?: StyleReferenceType
):
  | 'product-reference'
  | 'design-reference'
  | 'pattern-reference'
  | 'color-reference' {
  if (category === 'style') {
    return styleType === 'pattern' ? 'pattern-reference' : 'design-reference';
  }
  return `${category}-reference`;
}

export function partitionStyleReferenceUploads(
  uploads: Array<OrgChatAttachmentInput>
): {
  designUploads: Array<OrgChatAttachmentInput>;
  patternUploads: Array<OrgChatAttachmentInput>;
} {
  const designUploads: Array<OrgChatAttachmentInput> = [];
  const patternUploads: Array<OrgChatAttachmentInput> = [];

  for (const upload of uploads) {
    if (upload.referenceCategory === 'pattern-reference') {
      patternUploads.push(upload);
    } else {
      designUploads.push(upload);
    }
  }

  return { designUploads, patternUploads };
}

export type AttachmentIdentityInput = Pick<
  OrgChatAttachmentInput,
  | 'filename'
  | 'kind'
  | 'sizeBytes'
  | 'storageId'
  | 'storageKey'
  | 'previewUrl'
  | 'url'
  | 'vfsPath'
  | 'workspaceFileId'
>;
